import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Switch } from "@/components/ui/switch";
import { WorkspaceModulesEditor } from "@/components/WorkspaceModulesEditor";
import { toast } from "sonner";

const MODULE_ICONS = {
  plan: "Target", weekly_focus: "CalendarCheck", actions: "ListChecks", scorecard: "Gauge",
  coaching: "Users", business_health: "Activity", issues: "TriangleAlert",
  resources: "BookOpen", ask_amden: "Sparkles", reports: "BarChart3",
};

// Super-Admin control: turn client workspace modules on/off for a single organisation.
export function ModuleAccessEditor({ orgId, orgName }) {
  const { orgs, setOrgs } = useOrg();
  const [modules, setModules] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    api.get(`/organisations/${orgId}/modules`).then((r) => setModules(r.data.modules)).catch(() => setModules([]));
  }, [orgId]);

  const toggle = async (key, enabled) => {
    setSavingKey(key);
    // optimistic
    setModules((m) => m.map((x) => (x.key === key ? { ...x, enabled } : x)));
    try {
      const { data } = await api.put(`/organisations/${orgId}/modules`, { module_access: { [key]: enabled } });
      setModules(data.modules);
      const access = Object.fromEntries(data.modules.map((x) => [x.key, x.enabled]));
      setOrgs(orgs.map((o) => (o.id === orgId ? { ...o, module_access: { ...(o.module_access || {}), ...access } } : o)));
      toast.success(`${data.modules.find((x) => x.key === key)?.label} ${enabled ? "enabled" : "turned off"}`);
    } catch (e) {
      setModules((m) => m.map((x) => (x.key === key ? { ...x, enabled: !enabled } : x)));
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSavingKey(null); }
  };

  const offCount = (modules || []).filter((m) => !m.enabled).length;

  if (!modules) return <div className="py-8 text-center"><Icons.Loader2 className="mx-auto animate-spin text-[#FFB000]" size={22} /></div>;

  return (
    <div data-testid="module-access-editor">
      <div className="mb-3 flex items-center gap-2 rounded-lg bg-[#F7F9FC] px-3.5 py-2.5 text-xs text-[#1E2D44]/65">
        <Icons.Info size={14} className="shrink-0 text-[#B87F1E]" />
        <span>Turn client areas on or off{orgName ? ` for ${orgName}` : ""}. Home and Settings always stay on. Amden staff always keep full access.</span>
      </div>
      <div className="space-y-1.5">
        {modules.map((m) => (
          <div key={m.key} className="flex items-center gap-3 rounded-lg border border-[#E4E8EF] px-3.5 py-2.5" data-testid={`module-row-${m.key}`}>
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${m.enabled ? "bg-[#0B1729] text-[#FFC24D]" : "bg-[#F4F6F9] text-[#1E2D44]/35"}`}>
              {React.createElement(Icons[MODULE_ICONS[m.key]] || Icons.Circle, { size: 16 })}
            </span>
            <div className="min-w-0 flex-1">
              <div className={`text-sm font-medium ${m.enabled ? "text-[#0B1729]" : "text-[#1E2D44]/55"}`}>{m.label}</div>
              <div className="text-[11px] text-[#1E2D44]/45">{m.enabled ? "Visible to the client" : "Locked for the client"}</div>
            </div>
            {savingKey === m.key
              ? <Icons.Loader2 className="animate-spin text-[#FFB000]" size={16} />
              : <Switch checked={m.enabled} onCheckedChange={(v) => toggle(m.key, v)} data-testid={`module-toggle-${m.key}`} />}
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-[#1E2D44]/50">{offCount === 0 ? "All areas are enabled." : `${offCount} area${offCount === 1 ? "" : "s"} turned off for this client.`}</p>
      <WorkspaceModulesEditor orgId={orgId} />
    </div>
  );
}
